import { inject, Injectable } from '@angular/core';
import {
  AssetExpressionsParams,
  AssetField, PolicyFindingFilterField,
  EmPairingStatus,
  ExprOp,
  FindingField,
  FindingsObjExpressionsParams,
  FindingStatus,
  GetAssetsCountGQL,
  GetFindingsCountGQL,
  GetGroupedEventCountGQL,
  GetIcPsGQL,
  GetInventoryStatisticsGQL,
  GetRecentEventsGQL,
  GetSensorsSystemGQL,
  GroupedAssetExpressionsParams,
  GroupedEventsExpressionsParams,
  IemRecentEventsExpressionsParams,
  PluginSeverity,
  PluginSource,
  PolicyFindingsExpressionsParams, EventCategoryType, GetPolicyFindingsCountGQL,
} from '@uigy/generated/graphql';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TimeFrameItem, TimeFrameService } from '@uigy/shared/services/time-frame.service';
import { UiGridFilterModel } from '@uigy/shared/components/ui-grid/models/ui-grid';
import Utils from '@uigy/utils';
import { KpiSmallRatioData } from '@uigy/modules/overview/kpis/kpi.interfaces';

@Injectable({
  providedIn: 'root'
})
export class KpiWhatsNewService {
  private timeFrameService = inject(TimeFrameService);
  private getAssetsCountGQL = inject(GetAssetsCountGQL);
  private getFindingsCountGQL = inject(GetFindingsCountGQL);
  private getPolicyFindingsCountGQL = inject(GetPolicyFindingsCountGQL);
  private getInventoryStatisticsGQL = inject(GetInventoryStatisticsGQL);
  private getGroupedEventCountGQL = inject(GetGroupedEventCountGQL);
  private getRecentEventsGQL = inject(GetRecentEventsGQL);
  private getICPsGQL = inject(GetIcPsGQL);
  private getSensorsSystemGQL = inject(GetSensorsSystemGQL);

  constructor() {
  }

  public createFindingsFilter(value: TimeFrameItem, pluginSources: PluginSource[] = [PluginSource.Tot], forNavigation: boolean = false): UiGridFilterModel<FindingField> {
    const firstHit = forNavigation ?
      {
        field: FindingField.FindingFirstHit,
        op: ExprOp.GreaterEqual,
        timeFrame: value.value,
      }
      :
      {
        field: FindingField.FindingFirstHit,
        op: ExprOp.GreaterEqual,
        values: this.timeFrameService.timeFrameToDateTime(value).toISOString(),
      };

    return {
      op: ExprOp.And,
      expressions: [
        firstHit,
        {field: FindingField.FindingStatus, op: ExprOp.In, values: [FindingStatus.Active, FindingStatus.Resurfaced]},
        {field: FindingField.PluginSource, op: ExprOp.In, values: pluginSources},
        {
          field: FindingField.PluginSeverity,
          op: ExprOp.In,
          values: [PluginSeverity.Low, PluginSeverity.Medium, PluginSeverity.High, PluginSeverity.Critical],
        },
      ],
    };
  }

  public createAssetFilter(value: TimeFrameItem, field: AssetField): UiGridFilterModel<AssetField> {
    return {
      op: ExprOp.And,
      expressions: [
        {field, op: ExprOp.GreaterEqual, values: this.timeFrameService.timeFrameToDateTime(value).toISOString()},
        {field: AssetField.Hidden, op: ExprOp.Equal, values: false},
      ],
    };
  }

  public createHighRiskViolationFilter(value: TimeFrameItem, forNavigation: boolean = false): UiGridFilterModel<PolicyFindingFilterField> {
    const lastHit = forNavigation ?
      {
        field: PolicyFindingFilterField.LastHitTime,
        op: ExprOp.GreaterEqual,
        timeFrame: value.value,
      }
      :
      {
        field: PolicyFindingFilterField.LastHitTime,
        op: ExprOp.GreaterEqual,
        values: this.timeFrameService.timeFrameToDateTime(value).toISOString(),
      };

    return {
      op: ExprOp.And,

      expressions: [
        lastHit,
        {field: PolicyFindingFilterField.Severity, op: ExprOp.Equal, values: 'High'},
        {field: PolicyFindingFilterField.Status, op: ExprOp.In, values: [FindingStatus.Active, FindingStatus.Resurfaced]},
      ]
    };
  }

  public createOperationalViolationsFilter(value: TimeFrameItem, forNavigation: boolean = false): UiGridFilterModel<PolicyFindingFilterField> {
    const lastHit = forNavigation ?
      {
        field: PolicyFindingFilterField.LastHitTime,
        op: ExprOp.GreaterEqual,
        timeFrame: value.value,
      }
      :
      {
        field: PolicyFindingFilterField.LastHitTime,
        op: ExprOp.GreaterEqual,
        values: this.timeFrameService.timeFrameToDateTime(value).toISOString(),
      };

    return {
      op: ExprOp.And,

      expressions: [
        lastHit,
        {field: PolicyFindingFilterField.Category, op: ExprOp.In, values: [EventCategoryType.ConfigurationEvents,EventCategoryType.ScadaEvents]},
        {field: PolicyFindingFilterField.Status, op: ExprOp.In, values: [FindingStatus.Active, FindingStatus.Resurfaced]},
      ]
    };
  }

  public getFindingsCount(filter: FindingsObjExpressionsParams): Observable<number> {
    return this.getFindingsCountGQL.fetch({filter}).pipe(
      map(({data}) => data.findings.totalCount),
    );
  }

  public getAssetsCount(filter: AssetExpressionsParams): Observable<number> {
    return this.getAssetsCountGQL.fetch({filter}).pipe(
      map(({data}) => data.assetsForGrid.totalCount),
    );
  }

  public getHighRiskViolationCount(filter: PolicyFindingsExpressionsParams): Observable<number> {
    return this.getPolicyFindingsCountGQL.fetch({filter}).pipe(
      map(({data}) => data.count.totalCount),
    );
  }

  public getOperationalViolationsCount(filter: PolicyFindingsExpressionsParams): Observable<number> {
    return this.getPolicyFindingsCountGQL.fetch({filter}).pipe(
      map(({data}) => data.count.totalCount),
    );
  }

  public getGroupedAssetCount(filter: GroupedAssetExpressionsParams): Observable<number> {
    return this.getInventoryStatisticsGQL.fetch({filter}).pipe(
      map(({data}) => Utils.stripConnection(data.newAssets)?.[0]?.newAssets ?? 0)
    );
  }

  public getGroupedEventCount(filter: GroupedEventsExpressionsParams): Observable<number> {
    return this.getGroupedEventCountGQL.fetch({filter}).pipe(
      map(({data}) => Utils.stripConnection(data.groupedEventsRaw)?.[0]?.count ?? 0)
    );
  }

  public getRecentEventCount(filter: IemRecentEventsExpressionsParams): Observable<number> {
    return this.getRecentEventsGQL.fetch({filter}).pipe(
      map(({data}) => data.iemRecentEvents.totalCount),
    );
  }

  public getIcpCount(): Observable<KpiSmallRatioData> {
    return this.getICPsGQL.fetch().pipe(
      map(({data}) => {
        const connectedCount = Utils.stripConnection(data.emPairedIcps)?.filter(({status}) => status === EmPairingStatus.IcpConnected).length || 0;
        const totalCount = data.emPairedIcps?.totalCount ?? 0;

        return {
          currentValue: connectedCount,
          totalValue: totalCount
        };
      }),
    );
  }

  public getSensorCount(): Observable<KpiSmallRatioData> {
    return this.getSensorsSystemGQL.fetch().pipe(
      map(({data}) => {
        const onlineCount = Utils.stripConnection(data.online)?.[0]?.count ?? 0;
        const totalCount = Utils.stripConnection(data.totalCount)?.[0]?.count ?? 0;

        return {
          currentValue: onlineCount,
          totalValue: totalCount
        };
      }),
    );
  }
}
